import { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Alert,
} from 'antd';
import { PlusOutlined, ReloadOutlined, KeyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api, ApiClient } from '../../services/api';
import { DangerConfirmModal, DangerConfirmModalProps } from '../../components/DangerConfirmModal';

/** 需要二次确认的凭证操作，删除与密钥轮换为不可逆操作 */
type PendingAction = {
  kind: 'delete' | 'rotate' | 'permission' | 'status';
  row: ApiClient;
};

/** 各操作分支只需提供文案，其余 props 由页面统一传入 */
type ConfirmCopy = Omit<
  DangerConfirmModalProps,
  'open' | 'loading' | 'onOk' | 'onCancel'
>;

/** 无待确认操作时的占位文案，保证弹窗常驻以便播放关闭动画 */
const IDLE_COPY: ConfirmCopy = { title: '', warning: '', okText: '确认' };

const TYPE_OPTIONS = [
  { value: 'image', label: '图片' },
  { value: 'video', label: '视频' },
  { value: 'sfx', label: '音效' },
  { value: 'bgm', label: '音乐' },
];

const PERMISSION_OPTIONS = [
  { value: 'readwrite', label: '读写' },
  { value: 'readonly', label: '只读' },
];

function permissionLabel(v?: string) {
  return PERMISSION_OPTIONS.find((o) => o.value === v)?.label || '读写';
}

export default function ClientsPage() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ApiClient[]>([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [secretOnce, setSecretOnce] = useState<string | null>(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listClients({ page: 1, page_size: 100 });
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    const values = await form.validateFields();
    try {
      const res = await api.createClient({
        name: values.name,
        allowed_types: (values.allowed_types || []).join(','),
        permission: values.permission || 'readwrite',
        rate_limit: values.rate_limit || 0,
        remark: values.remark || '',
      });
      setSecretOnce(res.secret_key || null);
      message.success('已创建，请立即保存 SecretKey');
      setOpen(false);
      form.resetFields();
      load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '创建失败');
    }
  };

  /** 二次确认通过后真正执行操作 */
  const runPending = async () => {
    if (!pending) return;
    const { kind, row } = pending;
    setSubmitting(true);
    try {
      if (kind === 'delete') {
        await api.deleteClient(row.id);
        message.success('凭证已删除');
      } else if (kind === 'rotate') {
        const res = await api.updateClient(row.id, { rotate_secret: true });
        setSecretOnce(res.secret_key || null);
        message.success('密钥已轮换，旧 SecretKey 已失效');
      } else if (kind === 'permission') {
        const next = row.permission === 'readonly' ? 'readwrite' : 'readonly';
        await api.updateClient(row.id, { permission: next });
        message.success(next === 'readonly' ? '已改为只读' : '已改为读写');
      } else {
        const next = row.status === 'active' ? 'disabled' : 'active';
        await api.updateClient(row.id, { status: next });
        message.success(next === 'disabled' ? '已停用' : '已启用');
      }
      setPending(null);
      load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  /** 按操作类型生成强提示文案；删除与轮换密钥需手动输入名称才能确认 */
  const confirmCopy: ConfirmCopy | null = (() => {
    if (!pending) return null;
    const { kind, row } = pending;
    const target = [
      { label: 'ID', value: String(row.id) },
      { label: '名称', value: row.name },
      { label: 'AccessKey', value: row.access_key, copyable: true },
    ];
    switch (kind) {
      case 'delete':
        return {
          title: '删除凭证',
          warning: `确认删除凭证「${row.name}」？删除后无法恢复。`,
          details: [
            '该凭证的 AccessKey / SecretKey 将立即作废。',
            '所有使用该凭证的第三方应用会立即无法访问 OpenAPI。',
            '已上传的资源不会一并删除，但不再能通过该凭证访问。',
          ],
          target,
          confirmText: row.name,
          okText: '确认删除',
          alertType: 'error',
          okDanger: true,
        };
      case 'rotate':
        return {
          title: '轮换 SecretKey',
          warning: `确认轮换「${row.name}」的 SecretKey？`,
          details: [
            '新的 SecretKey 只显示一次，关闭后无法再次查看，请先准备好保存位置。',
            '旧 SecretKey 立即失效且无法恢复。',
            '未及时更新密钥的第三方应用会立即调用失败。',
          ],
          target,
          confirmText: row.name,
          okText: '确认轮换',
          alertType: 'error',
          okDanger: true,
        };
      case 'permission': {
        const toReadonly = row.permission !== 'readonly';
        return {
          title: toReadonly ? '改为只读权限' : '改为读写权限',
          warning: toReadonly
            ? `确认将「${row.name}」改为只读权限？`
            : `确认将「${row.name}」改为读写权限？`,
          details: toReadonly
            ? ['该凭证将只能查询资源。', '其上传等写操作会立即返回 403。']
            : ['读写凭证可以查询并上传资源。', '请确认该应用确实需要写权限。'],
          target,
          okText: toReadonly ? '确认改为只读' : '确认改为读写',
          alertType: 'warning',
          okDanger: false,
        };
      }
      default: {
        const toDisabled = row.status === 'active';
        return {
          title: toDisabled ? '停用凭证' : '启用凭证',
          warning: toDisabled ? `确认停用「${row.name}」？` : `确认启用「${row.name}」？`,
          details: toDisabled
            ? ['停用后该凭证的 OpenAPI 调用会立即被拒绝。', '重新启用后即可恢复访问。']
            : ['启用后该凭证将立即恢复访问 OpenAPI。'],
          target,
          okText: toDisabled ? '确认停用' : '确认启用',
          alertType: 'warning',
          okDanger: false,
        };
      }
    }
  })();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Typography.Title level={3} className="tech-title" style={{ margin: 0, color: '#fff' }}>
            凭证管理
          </Typography.Title>
          <Typography.Text type="secondary">
            HMAC-SHA256 签名协议 · 只读凭证仅可查询，不能上传或修改
          </Typography.Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({
                allowed_types: ['image', 'video', 'sfx', 'bgm'],
                permission: 'readwrite',
                rate_limit: 0,
              });
              setOpen(true);
            }}
          >
            新建客户端
          </Button>
        </Space>
      </div>

      {secretOnce && (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setSecretOnce(null)}
          style={{ marginBottom: 16 }}
          message="SecretKey 仅显示一次，请妥善保存"
          description={<Typography.Paragraph copyable>{secretOnce}</Typography.Paragraph>}
        />
      )}

      <div className="tech-panel" style={{ padding: 8 }}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={items}
          pagination={false}
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              width: 80,
            },
            { title: '名称', dataIndex: 'name' },
            {
              title: 'AccessKey',
              dataIndex: 'access_key',
              render: (v: string) => <Typography.Text copyable>{v}</Typography.Text>,
            },
            {
              title: '权限',
              dataIndex: 'permission',
              width: 90,
              render: (v: string) => (
                <Tag color={v === 'readonly' ? 'blue' : 'gold'}>{permissionLabel(v)}</Tag>
              ),
            },
            {
              title: '允许类型',
              dataIndex: 'allowed_types',
              render: (v: string) =>
                (v || '').split(',').map((t) => (
                  <Tag key={t}>{TYPE_OPTIONS.find((o) => o.value === t)?.label || t}</Tag>
                )),
            },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (v: string) => (
                <Tag color={v === 'active' ? 'success' : 'default'}>{v === 'active' ? '启用' : '停用'}</Tag>
              ),
            },
            {
              title: '限流',
              dataIndex: 'rate_limit',
              width: 100,
              render: (v: number) => (v > 0 ? `${v}/min` : '默认'),
            },
            {
              title: '创建时间',
              dataIndex: 'created_at',
              width: 170,
              render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
            },
            {
              title: '操作',
              width: 320,
              render: (_: unknown, row: ApiClient) => (
                <Space>
                  <Button
                    type="link"
                    icon={<KeyOutlined />}
                    onClick={() => setPending({ kind: 'rotate', row })}
                  >
                    轮换密钥
                  </Button>
                  <Button
                    type="link"
                    onClick={() => setPending({ kind: 'permission', row })}
                  >
                    {row.permission === 'readonly' ? '改为读写' : '改为只读'}
                  </Button>
                  <Button
                    type="link"
                    onClick={() => setPending({ kind: 'status', row })}
                  >
                    {row.status === 'active' ? '停用' : '启用'}
                  </Button>
                  <Button
                    type="link"
                    danger
                    onClick={() => setPending({ kind: 'delete', row })}
                  >
                    删除
                  </Button>
                </Space>
              ),
            },
          ]}
        />
        <div style={{ padding: '8px 12px', color: 'var(--text-dim)' }}>共 {total} 个客户端</div>
      </div>

      <Modal title="新建 OpenAPI 客户端" open={open} onCancel={() => setOpen(false)} onOk={create} destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="name" label="名称" rules={[{ required: true }]}>
            <Input placeholder="例如：剪辑客户端" />
          </Form.Item>
          <Form.Item name="permission" label="权限" rules={[{ required: true }]}>
            <Select options={PERMISSION_OPTIONS} />
          </Form.Item>
          <Form.Item name="allowed_types" label="允许资源类型" rules={[{ required: true }]}>
            <Select mode="multiple" options={TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item name="rate_limit" label="每分钟限流（0=全局默认）">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <DangerConfirmModal
        {...(confirmCopy ?? IDLE_COPY)}
        open={!!pending}
        loading={submitting}
        onOk={runPending}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}
